/**
 * ESTABLISHMENT.THUNK.TS
 * Custom endpoints
 */
import { createAsyncThunk } from "@reduxjs/toolkit"
import { instanceBackend } from "@/App"
import { fetchBackend } from "@/core/apis/backend"
import siteStore from "@/entities/site/model/site.store"
import physicalRiskStore from "@/entities/physical-risk/model/physical-risk.store"
import mapStore from "@/entities/map/model/map.store"
import populationStore from "@/entities/population/model/population.store"
import preventionActionStore from "@/entities/prevention-action/model/prevention-action.store"
import { PreventionAction } from "@/entities/prevention-action/model/prevention-action.types"
import riskFamilyStore from "@/entities/risk-family/model/risk-family.store"
import documentStore from "@/entities/document/model/document.store"
import establishmentStore from "./establishment.store"
import { Establishment } from "./establishment.types"
import {
  accountFetchRpsSurveys,
  accountGetRpsSurveys,
  accountRisksSettingsSet,
  accountUpdate
} from "@/redux/account.actions"
import { AccountOptions, createEmptyAccountRisksSettings, PendingTask } from "@/redux/account.types"
import { STATUS_LOADED, STATUS_LOADING } from "@/redux/_status.types"
import { RootState } from "@/core/root-reducer"
import { RpsRiskAxis } from "@/entities/population/model/rps-risk"
import { Population } from "@/entities/population/model/population.types"
import { sessionEdit } from "@/redux/_session.actions"
import cookie from "react-cookies"

//Change order
export const connect = createAsyncThunk("establishment/connect", async (establishmentId: string) => {
  return await fetchBackend({
    url: `/establishment/connect`,
    method: "POST",
    data: { establishmentId }
  })
})

//Les taches d'import vivent dans les options du compte : chaque reponse du back les remet en session
function setPendingTasks(dispatch: any, getState: any, pendingTasks: PendingTask[]) {
  const accountOptions = (getState() as RootState)._session.accountOptions
  dispatch(sessionEdit("accountOptions", new AccountOptions({ ...accountOptions, pendingTasks })))
}

function addPendingTask(dispatch: any, getState: any, task: PendingTask) {
  const accountOptions = (getState() as RootState)._session.accountOptions
  setPendingTasks(dispatch, getState, [
    ...(accountOptions.pendingTasks ?? []).filter((item) => item.id !== task.id),
    task
  ])
}

//Revue des risques de l'etablissement connecte
export const scan = createAsyncThunk("establishment/scan", async (_, { dispatch, getState }) => {
  const task = await fetchBackend({ url: "/establishment/scan", method: "POST" })
  if (!task.error) addPendingTask(dispatch, getState, task)
  return task
})

//Etat a jour des taches d'import, demande tant qu'une relecture tourne
export const fetchImportTasks = createAsyncThunk(
  "establishment/fetch-import-tasks",
  async (_, { dispatch, getState }) => {
    const tasks = await fetchBackend({ url: `/establishment/import-document/tasks` })
    if (Array.isArray(tasks)) setPendingTasks(dispatch, getState, tasks)
    return tasks
  }
)

//Resultat d'une tache d'import terminee, a relire avant l'import
export const fetchImportTaskResult = createAsyncThunk(
  "establishment/fetch-import-task-result",
  async (taskId: string, { dispatch, getState }) => {
    const result = await fetchBackend({ url: `/establishment/import-document/tasks/${taskId}` })
    const state = getState() as RootState
    const task = state._session.accountOptions.pendingTasks?.find((item) => item.id === taskId)
    if (task?.type === "establishment-scan" && Array.isArray(result)) {
      const establishment =
        state.establishment.active.id === task.EstablishmentId
          ? state.establishment.active
          : state.establishment.list.find((item) => item.id === task.EstablishmentId)
      if (establishment) dispatch(establishmentStore.actions.replace({ ...establishment, scanResults: result }))
    }
    return result
  }
)

//Retire une tache d'import (et arrete son extraction si elle tourne encore)
export const removeImportTask = createAsyncThunk(
  "establishment/remove-import-task",
  async (taskId: string, { dispatch, getState }) => {
    const tasks = await fetchBackend({ url: `/establishment/import-document/tasks/${taskId}`, method: "DELETE" })
    if (Array.isArray(tasks)) setPendingTasks(dispatch, getState, tasks)
    return tasks
  }
)

//Objets extraits du document unique, apres relecture par l'utilisateur
export const importDocumentConfirm = createAsyncThunk("establishment/import-document-confirm", async (data: any) => {
  return await fetchBackend({
    url: `/establishment/import-document/confirm`,
    method: "POST",
    data
  })
})

//Vide les donnees risques (tout le compte en mono-etablissement, baremes compris), puis recharge les stores
export const clearData = createAsyncThunk("establishment/clear-data", async (_, { dispatch, getState }) => {
  const response = await fetchBackend({ url: `/establishments/data`, method: "DELETE" })
  if (response.error) return response

  //Mono : baremes et attributs reviennent aux valeurs par defaut, l'organisation reste (sans reglages, la home la croit absente)
  const settings = (getState() as RootState)._session.accountRisksSettings
  if (!settings.allowMultipleEstablishments) {
    dispatch(accountRisksSettingsSet(createEmptyAccountRisksSettings({ legalIdentifier: settings.legalIdentifier, residualRiskLevels: [] })))
    await dispatch(accountUpdate((getState() as RootState).account.active))
  }
  await dispatch(fetchData())
  return response
})

export const enterContext = createAsyncThunk(
  "establishment/enter-context",
  async (establishment: Establishment, { dispatch }) => {
    const response = await fetchBackend({
      url: `/establishment/connect`,
      method: "POST",
      data: { establishmentId: establishment.id }
    })

    if (response.error) return response

    instanceBackend.defaults.headers.common["idtreeToken"] = response
    cookie.save("idtreeToken", response, { path: "/" })
    cookie.save("idtreeEstablishmentId", establishment.id, { path: "/" })

    dispatch(establishmentStore.actions.setActive(establishment))
    dispatch(sessionEdit("establishmentId", establishment.id))
    dispatch(sessionEdit("risksInterfaceType", "ESTABLISHMENT"))

    return response
  }
)

/**
 * Fetch data for the establishment
 */
export const fetchData = createAsyncThunk("establishment/fetch-data", async (_, { getState, dispatch }) => {
  dispatch(establishmentStore.actions.setStatus(STATUS_LOADING))

  //Le store est persiste dans le localStorage : sans reset, les enquetes d'un chargement
  //precedent afficheraient leurs taches avant la reponse du serveur.
  dispatch(accountGetRpsSurveys([]))

  //Fetch all data
  await dispatch(siteStore.thunks.fetchAndSet())
  await dispatch(physicalRiskStore.thunks.fetchAndSet())
  await dispatch(mapStore.thunks.fetchAndSet())
  await dispatch(populationStore.thunks.fetchAndSet())
  await dispatch(preventionActionStore.thunks.fetchAndSet())
  await dispatch(riskFamilyStore.thunks.fetchAndSet())
  await dispatch(documentStore.thunks.fetchAndSet())

  //Set active map
  const state = getState() as RootState
  const maps = state.map.list
  dispatch(mapStore.actions.setActive(maps[0]))

  //Enquetes RPS du compte : alimente la tache "enquete a traiter" du panneau de notifications,
  //que la page n'attend pas pour s'afficher
  dispatch(accountFetchRpsSurveys())

  //End loading
  dispatch(establishmentStore.actions.setStatus(STATUS_LOADED))
})

//On page change reset the active item and the selected item
export const resetActiveItems = createAsyncThunk(
  "establishment/reset-active-items",
  async (search: string, { dispatch, getState }) => {
    const state = getState() as RootState

    if (search.length) {
      const queryParams = new URLSearchParams(search)

      //Detect query params
      const siteId = queryParams.get("siteId")
      const populationId = queryParams.get("populationId")
      const riskFamily = queryParams.get("riskFamilyId")
      const physicalRiskId = queryParams.get("physicalRiskId")

      //Find items
      const site = siteId ? (state.site.list.find((x) => x.id === siteId) ?? null) : null
      const population = populationId ? (state.population.list.find((x) => x.id === populationId) ?? null) : null
      const family = riskFamily
        ? (state.riskFamily.list.find((x) => x.id === riskFamily || x.code === riskFamily) ?? null)
        : null
      const risk = physicalRiskId ? (state.physicalRisk.list.find((x) => x.id === physicalRiskId) ?? null) : null

      //Update store according query params
      dispatch(siteStore.actions.setActive(site))
      dispatch(populationStore.actions.setActive(population))
      dispatch(riskFamilyStore.actions.setActive(family))
      dispatch(physicalRiskStore.actions.setActive(risk))
    } else {
      dispatch(siteStore.actions.setActive(null))
      dispatch(populationStore.actions.setActive(null))
      dispatch(riskFamilyStore.actions.setActive(null))
      dispatch(physicalRiskStore.actions.setActive(null))
    }

    //Clean select
    dispatch(physicalRiskStore.actions.select([]))
    dispatch(siteStore.actions.select([]))
    dispatch(populationStore.actions.select([]))
    dispatch(riskFamilyStore.actions.select([]))

    return true
  }
)

export const mergeRisks = createAsyncThunk(
  "physicalRisk/merge",
  async (input: string | { riskIds: string[]; targetId: string }, { dispatch, getState }) => {
    const establishmentId = (getState() as RootState)._session.establishmentId
    const response = await fetchBackend({
      url: "/physicalRisk/merge",
      method: "POST",
      data: typeof input === "string" ? { suggestionId: input } : input
    })
    if (response.error) return response
    const state = getState() as RootState
    if (state._session.establishmentId !== establishmentId) return response
    const kept = state.physicalRisk.list.find((risk) => risk.id === response.keptId)
    if (kept) {
      dispatch(
        physicalRiskStore.actions.replace({
          ...kept,
          siteIds: response.siteIds,
          populationIds: response.populationIds,
          populationEvaluations: response.populationEvaluations,
          prevention: response.prevention,
          updatedAt: new Date(response.updatedAt)
        })
      )
    }
    dispatch(physicalRiskStore.actions.removeMany(response.removedIds))
    const removedActionIds: string[] = response.removedActionIds ?? []
    dispatch(preventionActionStore.actions.removeMany(removedActionIds))
    const mergedRiskIds = new Set([response.keptId, ...response.removedIds])
    dispatch(
      preventionActionStore.actions.setList([
        ...state.preventionAction.list.filter((action) => !mergedRiskIds.has(action.PhysicalRiskId)),
        ...response.preventionActions
      ])
    )
    const establishment =
      state.establishment.active.id === establishmentId
        ? state.establishment.active
        : state.establishment.list.find((item) => item.id === establishmentId)
    if (establishment)
      dispatch(establishmentStore.actions.replace({ ...establishment, scanResults: response.scanResults }))
    return response
  }
)

//Passe un risque dans la matrice RPS : ses fonctions recoivent une situation, ses mesures deviennent des mesures RPS
//de ces fonctions, le risque disparait
export const moveRiskToRps = createAsyncThunk(
  "physicalRisk/to-rps",
  async (input: { riskId: string; axis: RpsRiskAxis }, { dispatch, getState }) => {
    const establishmentId = (getState() as RootState)._session.establishmentId
    const response = await fetchBackend({ url: "/physicalRisk/to-rps", method: "POST", data: input })
    if (response.error) return response
    const state = getState() as RootState
    if (state._session.establishmentId !== establishmentId) return response
    dispatch(physicalRiskStore.actions.removeMany([response.riskId]))
    const movedActions = new Map<string, PreventionAction>(
      response.preventionActions.map((action: PreventionAction) => [action.id, action])
    )
    dispatch(
      preventionActionStore.actions.setList(
        state.preventionAction.list.map((action) => movedActions.get(action.id) ?? action)
      )
    )
    response.populations.forEach((population: Population) => dispatch(populationStore.actions.replace(population)))
    const establishment = state.establishment.active
    if (establishment.id === establishmentId)
      dispatch(establishmentStore.actions.replace({ ...establishment, scanResults: response.scanResults }))
    return response
  }
)
